import * as React from "react";
import {
    IClient,
    IClientBooking,
    IIntakeSessionHistory,
} from "intake_shared/src/types";
import styles from "./AppointmentHistory.module.scss";
import { cn } from "../../../../helpers";
import EmptyListState from "../../../bits/EmptyListState/EmptyListState";
import IntakeSessionHistoryCard from "./AppointmentCard/IntakeSessionHistoryCard";
import { CacheContext } from "../../../../../contexts/CacheContext";
import { fetchCliniciansByIds } from "../../../../../store/clinician";

interface IntakeSessionHistoryProps {
    initialHistories?: IIntakeSessionHistory[];
    client?: IClient;
    showHeader?: boolean;
    viewFrom?: "side-bar" | "full-page";
}

export default function IntakeSessionHistory({
    initialHistories = [],
    client,
    showHeader = true,
    viewFrom = "side-bar",
}: IntakeSessionHistoryProps) {
    const olc = React.useContext(CacheContext);
    const allDepartmentsAndServices = olc?.allDepartmentAndServices || {};
    const [expandedCards, setExpandedCards] = React.useState<{
        [id: string]: boolean;
    }>({});
    const [clinicianCredentialById, setClinicianCredentialById] = React.useState<
        Record<string, string>
    >({});

    const handleToggleExpand = (id: string) => {
        setExpandedCards((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    React.useEffect(() => {
        const fetchClinicianCredentials = async () => {
            const supervisorIds = Array.from(
                new Set(initialHistories.map((h) => h.supervisor_id).filter(Boolean))
            );
            if (supervisorIds.length === 0) {
                setClinicianCredentialById({});
                return;
            }

            const clinicians = await fetchCliniciansByIds([...supervisorIds]);
            const credentialsMap = clinicians.reduce<Record<string, string>>(
                (acc, clinician) => {
                    if (clinician.id && clinician.credential) {
                        acc[clinician.id] = clinician.credential;
                    }
                    return acc;
                },
                {}
            );
            setClinicianCredentialById(credentialsMap);
        };

        fetchClinicianCredentials();
    }, [initialHistories]);

    const bookingById = React.useMemo<Record<string, IClientBooking>>(() => {
        if (!client) {
            return {};
        }

        return [
            ...(client.confirmed_bookings || []),
            ...(client.held_bookings || []),
            ...(client.appointment_history || []),
        ]
            .filter((booking) => booking?.id)
            .reduce<Record<string, IClientBooking>>((acc, booking) => {
                acc[booking.id] = booking;
                return acc;
            }, {});
    }, [client]);

    const intakeHistoriesWithCredential = initialHistories.map((history) => ({
        ...history,
        clinician_credential: clinicianCredentialById[history.supervisor_id] || "",
    }));

    return initialHistories && initialHistories.length > 0 ? (
        <div
            className={cn(
                styles.appointmentHistoryContainer,
                viewFrom === "full-page" && styles.appointmentHistoryContainerPage
            )}
        >
            {showHeader && (
                <div className={styles.appointmentHistoryHeader}>
                    <h6>Intake Session History ({initialHistories.length})</h6>
                </div>
            )}
            <div className={styles.appointmentHistoryList}>
                {intakeHistoriesWithCredential.map((appointment, index) => (
                    <IntakeSessionHistoryCard
                        key={`intake-session-${appointment.id}-${index}`}
                        appointment={appointment}
                        isExpanded={expandedCards[appointment.id] || false}
                        onToggleExpand={() => handleToggleExpand(appointment.id)}
                        viewFrom={viewFrom}
                        allDepartmentsAndServices={allDepartmentsAndServices}
                        attachedAppointment={bookingById[appointment.booking_id]}
                    />
                ))}
            </div>
        </div>
    ) : (
        <EmptyListState
            emptyMessage="No appointment history"
            stateHelperText="No appointment history found"
        />
    );
}
